import { useEffect, useRef } from 'react'

export const useCheckIframeExistByName = (
  iframeNameToCheck: string,
  bodyClassName: string = 'iframe-present',
): void => {
  const observerRef = useRef<MutationObserver | null>(null)

  useEffect(() => {
    const checkAndApplyClass = () => {
      const iframeElement = document.querySelector(`iframe[name="${iframeNameToCheck}"]`)
      const iframeExists = !!iframeElement // Convert to boolean

      if (iframeExists) {
        document.body.classList.add(bodyClassName)
      } else {
        // If the iframe is not found, ensure the class is removed
        document.body.classList.remove(bodyClassName)
      }
    }

    checkAndApplyClass()

    // As iframe loaded synchronously, Set up MutationObserver to watch for DOM changes
    // We observe the body, as the iframe will be a child of it (or a child of a child)
    observerRef.current = new MutationObserver(mutationsList => {
      let iframeChanged = false
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          // Check addedNodes for potential iframes
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement
              // If an iframe is added, or an element containing an iframe is added
              if (element.tagName === 'IFRAME' && element.getAttribute('name') === iframeNameToCheck) {
                iframeChanged = true
                break // Found our specific iframe added
              }
              if (element.querySelector(`iframe[name="${iframeNameToCheck}"]`)) {
                iframeChanged = true
                break // Found our specific iframe within an added element
              }
            }
          }
          if (iframeChanged) break // No need to check removedNodes if already found an addition

          // Check removedNodes for potential iframes
          for (const node of mutation.removedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement
              // If an iframe is removed, or an element containing an iframe is removed
              if (element.tagName === 'IFRAME' && element.getAttribute('name') === iframeNameToCheck) {
                iframeChanged = true
                break
              }
              if (element.querySelector(`iframe[name="${iframeNameToCheck}"]`)) {
                iframeChanged = true
                break
              }
            }
          }
          if (iframeChanged) break // Break outer loop if recheck is needed
        }
      }

      if (iframeChanged) {
        // Re-check the iframe existence after a relevant DOM change
        checkAndApplyClass()
      }
    })

    // Just observing the document body for child list changes (nodes added/removed)
    // and optionally attribute changes if iframe name can change
    observerRef.current.observe(document.body, { childList: true, subtree: true })

    // Cleanup function, to just make sure we remove.
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect() // Stop observing
      }
      document.body.classList.remove(bodyClassName)
    }
  }, [iframeNameToCheck, bodyClassName])
}
